import type { BigIntish } from '@morpho-org/blue-sdk'

import { toBigInt } from '@morpho-org/dapp-core'
import { formatDuration, intervalToDuration } from 'date-fns'
import { enUS } from 'date-fns/locale'

export type TimelockCriticality = 'high' | 'normal'

export interface TimelockFunctionConfig {
  label: string
  tooltipContent?: string
  criticality?: TimelockCriticality
}

/**
 * Gets the criticality level for a given timelock function name.
 * Returns "normal" as default if the function name is not found.
 */
export const getTimelockCriticality = (
  functionName: string,
  config: Record<string, TimelockFunctionConfig>
): TimelockCriticality => {
  return config[functionName]?.criticality ?? 'normal'
}

/**
 * Sorts pending timelocks by criticality (high first) then by validAt (ascending).
 */
export const sortPendingTimelocks = <T extends { functionName: string; validAt: BigIntish }>(
  items: T[],
  config: Record<string, TimelockFunctionConfig>
): T[] => {
  return [...items].toSorted((a, b) => {
    const criticalityA = getTimelockCriticality(a.functionName, config) === 'high' ? 0 : 1
    const criticalityB = getTimelockCriticality(b.functionName, config) === 'high' ? 0 : 1

    // Sort by criticality first (high criticality = 0, normal = 1)
    if (criticalityA !== criticalityB) {
      return criticalityA - criticalityB
    }

    // Then sort by validAt
    return Number(toBigInt(a.validAt) - toBigInt(b.validAt))
  })
}

/**
 * Format duration from seconds to a precise human-readable string.
 *
 * @param duration - The input BigIntish duration in seconds.
 * @returns A string representing the duration (e.g., "1 month", "3 days", "2 hours").
 *
 * @example
 * formatPreciseDuration(2592000n) => returns "1 month"
 * formatPreciseDuration(259200n) => returns "3 days"
 */
export const formatPreciseDuration = (duration: BigIntish) => {
  const durationObject = intervalToDuration({
    start: 0,
    end: Number(duration) * 1000
  })

  const { months = 0 } = durationObject

  return (
    formatDuration(durationObject, {
      locale: enUS,
      format: months > 0 ? ['months'] : undefined
    }) || 'No timelock'
  )
}
